import { useHead } from '@unhead/vue'
import { computed, unref } from 'vue'
import { useSanitizeHtml } from './useSanitizeHtml'
import { BASE_URL } from '@/constants/seo'

const { stripHtml } = useSanitizeHtml()

/**
 * Inyecta uno o más schemas JSON-LD en el head
 * @param {Object|Object[]|Ref} schemas - Schema(s) a inyectar
 */
export function useSchemaOrg(schemas) {
  const resolvedSchemas = computed(() => {
    const s = unref(schemas)
    const arr = Array.isArray(s) ? s : [s]
    return arr.filter(Boolean).map(cleanSchema)
  })

  useHead({
    script: computed(() =>
      resolvedSchemas.value.map(schema => ({
        type: 'application/ld+json',
        innerHTML: JSON.stringify(schema)
      }))
    )
  })
}

/**
 * Elimina campos undefined/null recursivamente
 */
function cleanSchema(obj) {
  if (obj === null || obj === undefined) return undefined
  if (Array.isArray(obj)) {
    const cleaned = obj.map(cleanSchema).filter(v => v !== undefined)
    return cleaned.length > 0 ? cleaned : undefined
  }
  if (typeof obj === 'object') {
    const cleaned = {}
    for (const [key, value] of Object.entries(obj)) {
      const cleanedValue = cleanSchema(value)
      if (cleanedValue !== undefined && cleanedValue !== '') {
        cleaned[key] = cleanedValue
      }
    }
    return Object.keys(cleaned).length > 0 ? cleaned : undefined
  }
  return obj
}

/**
 * Schema de Organization (global)
 */
export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${BASE_URL}/#organization`,
  name: 'Olvidadme',
  url: BASE_URL,
  logo: {
    '@type': 'ImageObject',
    url: `${BASE_URL}/logo.png`,
    width: 512,
    height: 512
  },
  description: 'Plataforma de servicios legales digitales para ejercer el derecho al olvido (GDPR Art. 17), gestión de legado digital post-mortem y testamento digital en España',
  email: 'contacto@olvidadme.com',
  address: {
    '@type': 'PostalAddress',
    addressCountry: 'ES',
    addressLocality: 'España'
  },
  areaServed: {
    '@type': 'Country',
    name: 'Spain'
  },
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    email: 'contacto@olvidadme.com',
    availableLanguage: 'es'
  },
  knowsAbout: [
    'GDPR Article 17 (Right to be Forgotten)',
    'Data Protection',
    'Digital Legacy'
  ]
}

/**
 * Genera schema LegalService para un servicio
 */
export function createLegalServiceSchema(service) {
  return {
    '@context': 'https://schema.org',
    '@type': 'LegalService',
    '@id': `${BASE_URL}${service.url}#service`,
    name: service.name,
    description: service.description,
    url: `${BASE_URL}${service.url}`,
    provider: { '@id': `${BASE_URL}/#organization` },
    areaServed: {
      '@type': 'Country',
      name: 'Spain'
    },
    areaOfLaw: service.areaOfLaw || ['GDPR', 'Data Protection Law'],
    serviceType: service.serviceType,
    offers: {
      '@type': 'Offer',
      price: service.price,
      priceCurrency: 'EUR',
      availability: 'https://schema.org/InStock',
      priceValidUntil: '2026-12-31'
    },
    image: service.image ? {
      '@type': 'ImageObject',
      url: service.image,
      width: 1200,
      height: 628
    } : undefined,
    availableLanguage: 'es'
  }
}

/**
 * Genera schema BlogPosting para un post
 */
export function createBlogPostingSchema(post) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    '@id': `${BASE_URL}/blog/${post.slug}#article`,
    headline: post.title,
    description: post.excerpt || post.metaDescription,
    datePublished: post.publishedAt,
    dateModified: post.updatedAt || post.publishedAt,
    author: {
      '@type': 'Person',
      name: post.author?.name || 'Equipo Olvidadme'
    },
    publisher: { '@id': `${BASE_URL}/#organization` },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${BASE_URL}/blog/${post.slug}`
    },
    image: post.featuredImage?.url ? {
      '@type': 'ImageObject',
      url: post.featuredImage.url,
      width: 1200,
      height: 628
    } : undefined,
    articleSection: post.category?.name,
    wordCount: post.wordCount,
    inLanguage: 'es'
  }
}

/**
 * Genera schema FAQPage
 */
export function createFAQSchema(questions) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: questions.map(q => ({
      '@type': 'Question',
      name: q.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: stripHtml(q.answer)
      }
    }))
  }
}

export default useSchemaOrg
